import ContentDisposition from "content-disposition";
import http from "./http";
import { createError } from "../errors/errors";
import Cookies from "../cookies";

type RedirectResponse<T> = T & {
    redirectUrl?: string;
};

/**
 * Refreshes a stale token with a new token to be used in subsequent requests
 * @param response result of the API request
 */
export function refreshToken(response: Response): void {
    const newToken = response.headers.get("Zeplin-Token");

    if (newToken) {
        Cookies.set("userToken", newToken);
        Zeplin.headers.set("Zeplin-Token", newToken);
    }
}

/**
 * Handles response by checking response status;
 * If response is ok; returns body json object else creates an error
 * @param {Response} response fetch response
 */
export function handleResponse<T>(response: Response): Promise<T> {
    refreshToken(response);
    return response.json().then(resJson => {
        if (response.ok) {
            return resJson as T;
        }
        throw createError(response.status, resJson);
    });
}

/**
 * Handles response by checking redirectUrl;
 * If redirectUrl is not null; returns null and redirects page
 * @param {Response} response fetch response
 */
export async function handleResponseWithRedirect<T>(response: RedirectResponse<T>): Promise<T | null> {
    const resJson = await response;

    if (resJson.redirectUrl) {
        window.location.assign(resJson.redirectUrl);
        return null;
    }

    return resJson;
}

/**
 * Handles response only if status code is 200, otherwise returns null
 * @param {Response} response fetch response
 */
export function handleResponseSilently<T>(response: Response): Promise<T> | null {
    if (response.status === http.STATUS.OK) {
        return handleResponse(response);
    }

    return null;
}

/**
 * Handles response by checking response status;
 * If response is ok; returns blob else creates an error
 * @param {Response} response fetch response
 */
export function handleResponseBlob(response: Response): Promise<{ blob: Blob; name: string; }> {
    refreshToken(response);
    return response.blob().then(resBlob => {
        if (response.ok) {
            const contentDispositionHeader = response.headers.get("content-disposition");
            let name;

            if (contentDispositionHeader) {
                name = ContentDisposition.parse(contentDispositionHeader)?.parameters?.filename;
            }

            return {
                blob: resBlob,
                name
            };
        }
        throw createError(response.status);
    });
}

/**
 * Throws error with json detail when response is not ok. (status is not in the range 200-299)
 * @param {Response} response fetch response
 */
export function checkResponseError(response: Response): Promise<never> | undefined {
    refreshToken(response);

    if (response.ok) {
        return;
    }

    return response.json().then(resJson => {
        throw createError(response.status, resJson);
    });
}

/**
 * Throws error with json detail when response is not ok. (status is not in the range 200-299)
 * Redirects if response has redirectUrl
 * @param {Response} response fetch response
 */
export async function checkResponseErrorWithRedirect(response: Response): Promise<void | boolean> {
    refreshToken(response);
    if (response.status === http.STATUS.OK || response.status === http.STATUS.CREATED) {
        const resJson = await response.json();
        if (resJson.redirectUrl) {
            window.location.assign(resJson.redirectUrl);
            return true;
        }
    }

    if (response.ok) {
        return;
    }

    return response.json().then(resJson => {
        throw createError(response.status, resJson);
    });
}
